import React, { useState, useRef, useEffect } from 'react';
import { XMarkIcon, CurrencyDollarIcon, SpeakerWaveIcon, MusicalNoteIcon, PlusIcon, PlayIcon, PauseIcon } from '@heroicons/react/24/outline';
import { IAudioGeneration, IUserModel } from '@packages/global';
import { useTranslation } from 'react-i18next';
import { useCoinStore } from '../../stores/coinStore';
import AudioUpload from '../common/AudioUpload';
import AudioTabs from '../common/AudioTabs';
import { EmotionSelector, AlphaSlider, SpeedSlider, ModelSelector } from '../common/form';

interface AudioGenerationFormData {
  prompt: string;
  spkAudioUrl: string;
  emoAudioUrl?: string;
  emoAlpha?: number;
  emoText?: string;
  useRandom?: boolean;
  maxTextTokensPerSegment?: number;
  matchReferenceDuration?: boolean;
  modelId: string;
  speedFactor?: number;
}

interface AudioGenerationFormProps {
  editingItem: IAudioGeneration | null;
  formData: AudioGenerationFormData;
  models: IUserModel[];
  modelsLoading: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onFormDataChange: (data: Partial<AudioGenerationFormData>) => void;
  onReset: () => void;
}

export default function AudioGenerationForm({
  editingItem,
  formData,
  models,
  modelsLoading,
  onSubmit,
  onFormDataChange,
  onReset
}: AudioGenerationFormProps) {
  const { t } = useTranslation(['generation', 'common']);
  const { account } = useCoinStore();
  const selectedModel = models.find(m => m.id === formData.modelId);
  const modelPrice = selectedModel?.coinPrice ?? 0;

  // 按字符数预估时长和费用（约10字符/秒）
  const CHARS_PER_SECOND = 10;
  const estimatedSeconds = formData.prompt.length > 0
    ? Math.ceil(formData.prompt.length / CHARS_PER_SECOND / (formData.speedFactor || 1))
    : 0;
  const estimatedCost = modelPrice * estimatedSeconds;
  const hasEnoughCoins = (account?.balance ?? 0) >= estimatedCost;

  // 弹出面板状态
  const [showSpkAudioUpload, setShowSpkAudioUpload] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const emoAudioRef = useRef<HTMLAudioElement | null>(null);
  const [emoAudioPlaying, setEmoAudioPlaying] = useState(false);

  // spkAudio URL 变化时重置播放状态
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = formData.spkAudioUrl || '';
    }
    setAudioPlaying(false);
  }, [formData.spkAudioUrl]);

  // emoAudio URL 变化时重置播放状态
  useEffect(() => {
    if (emoAudioRef.current) {
      emoAudioRef.current.pause();
      emoAudioRef.current.src = formData.emoAudioUrl || '';
    }
    setEmoAudioPlaying(false);
  }, [formData.emoAudioUrl]);

  const toggleAudioPlay = () => {
    if (!formData.spkAudioUrl) return;
    if (!audioRef.current) {
      audioRef.current = new Audio(formData.spkAudioUrl);
      audioRef.current.onended = () => setAudioPlaying(false);
    }
    if (audioPlaying) {
      audioRef.current.pause();
      setAudioPlaying(false);
    } else {
      audioRef.current.play();
      setAudioPlaying(true);
    }
  };

  const toggleEmoAudioPlay = () => {
    if (!formData.emoAudioUrl) return;
    if (!emoAudioRef.current) {
      emoAudioRef.current = new Audio(formData.emoAudioUrl);
      emoAudioRef.current.onended = () => setEmoAudioPlaying(false);
    }
    if (emoAudioPlaying) {
      emoAudioRef.current.pause();
      setEmoAudioPlaying(false);
    } else {
      emoAudioRef.current.play();
      setEmoAudioPlaying(true);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(e);
  };

  const hasEmotion = formData.emoText || formData.emoAudioUrl;

  return (
    <div className="w-96 lg:w-[420px] bg-bg-form border-r border-border-subtle flex flex-col">
      <AudioTabs />

      <div className="flex-1 overflow-y-auto p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* 标题行：重置按钮 */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-300">{t('audio.form.title')}</span>
            {editingItem && (
              <button
                type="button"
                onClick={onReset}
                className="p-1.5 text-gray-400 hover:text-gray-200 hover:bg-bg-elevated rounded-lg transition-colors"
                title={t('audio.form.resetForm')}
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* 模型选择 */}
          <ModelSelector
            models={models}
            selectedModelId={formData.modelId}
            onChange={(modelId) => onFormDataChange({ modelId })}
            loading={modelsLoading}
            placeholder={t('audio.form.selectModel')}
          />

          {/* 输入容器 */}
          <div className="bg-bg-surface rounded-2xl p-4 border border-border-subtle transition-colors focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
            {/* 文本内容输入 */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500">{formData.prompt.length} / 500</span>
              </div>
              <textarea
                value={formData.prompt}
                onChange={(e) => onFormDataChange({ prompt: e.target.value })}
                maxLength={500}
                rows={4}
                required
                className="w-full bg-transparent border-none focus:ring-0 text-gray-50 placeholder-gray-500 text-base resize-none py-0"
                placeholder={t('audio.form.promptPlaceholder')}
              />
            </div>

            {/* 参考音频区域 - 固定显示 */}
            <div className="mt-3 pt-3 border-t border-border-subtle relative">
              <div className="flex gap-2 flex-wrap items-center">
                {/* 已上传的参考音频 */}
                {formData.spkAudioUrl && (
                  <div className="relative group flex items-center gap-2 bg-bg-elevated rounded-lg px-3 py-2">
                    <button
                      type="button"
                      onClick={toggleAudioPlay}
                      className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-green-400 hover:text-green-300 transition-colors"
                      title={audioPlaying ? 'Pause' : 'Play'}
                    >
                      {audioPlaying
                        ? <PauseIcon className="w-4 h-4" />
                        : <PlayIcon className="w-4 h-4" />
                      }
                    </button>
                    <span className="text-xs text-gray-300">{t('audio.form.referenceAudio')}</span>
                    <button
                      type="button"
                      onClick={() => onFormDataChange({ spkAudioUrl: '', matchReferenceDuration: false })}
                      className="p-0.5 text-gray-400 hover:text-red-400 transition-colors"
                    >
                      <XMarkIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}

                {/* 已上传的情感音频 */}
                {formData.emoAudioUrl && (
                  <div className="relative group flex items-center gap-2 bg-purple-600/20 rounded-lg px-3 py-2">
                    <button
                      type="button"
                      onClick={toggleEmoAudioPlay}
                      className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-purple-400 hover:text-purple-300 transition-colors"
                      title={emoAudioPlaying ? 'Pause' : 'Play'}
                    >
                      {emoAudioPlaying
                        ? <PauseIcon className="w-4 h-4" />
                        : <PlayIcon className="w-4 h-4" />
                      }
                    </button>
                    <span className="text-xs text-purple-300">{t('audio.form.emotionAudio')}</span>
                    <button
                      type="button"
                      onClick={() => onFormDataChange({ emoAudioUrl: '' })}
                      className="p-0.5 text-purple-400 hover:text-red-400 transition-colors"
                    >
                      <XMarkIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}

                {/* 添加按钮 - 没有参考音频时显示 */}
                {!formData.spkAudioUrl && (
                  <button
                    type="button"
                    onClick={() => setShowSpkAudioUpload(true)}
                    className="w-12 h-12 bg-bg-elevated hover:bg-border-default rounded-lg flex items-center justify-center transition-colors border-2 border-dashed border-border-default"
                    title={t('audio.form.addReferenceAudio')}
                  >
                    <PlusIcon className="h-5 w-5 text-gray-400" />
                  </button>
                )}

                {/* 提示文字 */}
                <span className="text-xs text-gray-500">
                  {formData.spkAudioUrl ? '' : t('audio.form.referenceAudioRequired')}
                </span>
              </div>

              {/* 音频上传弹出面板 */}
              {showSpkAudioUpload && !formData.spkAudioUrl && (
                <div className="absolute bottom-full left-0 mb-2 bg-bg-surface border border-border-subtle rounded-lg shadow-lg z-50 p-3 min-w-[280px]">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs text-gray-400">{t('audio.form.uploadReferenceAudio')}</span>
                    <button
                      type="button"
                      onClick={() => setShowSpkAudioUpload(false)}
                      className="text-gray-500 hover:text-gray-300"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                  <AudioUpload
                    showToneSelector={true}
                    value=""
                    onUploadSuccess={(url) => {
                      onFormDataChange({ spkAudioUrl: url });
                      setShowSpkAudioUpload(false);
                    }}
                    placeholder={t('audio.form.uploadSpkAudio')}
                  />
                </div>
              )}
            </div>

            {/* 底部工具栏 */}
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border-subtle flex-wrap">
              {/* 情感选择器 */}
              {!formData.matchReferenceDuration && (
                <EmotionSelector
                  emoText={formData.emoText || ''}
                  emoAudioUrl={formData.emoAudioUrl}
                  onEmoTextChange={(text) => onFormDataChange({ emoText: text })}
                  onEmoAudioChange={(url) => onFormDataChange({ emoAudioUrl: url, emoText: url ? '' : formData.emoText })}
                  showAudioUpload={true}
                />
              )}

              {/* 情感强度滑块 */}
              {hasEmotion && !formData.matchReferenceDuration && (
                <AlphaSlider
                  value={formData.emoAlpha || 0.5}
                  onChange={(value) => onFormDataChange({ emoAlpha: value })}
                />
              )}

              {/* 语速控制 */}
              {!formData.matchReferenceDuration && (
                <SpeedSlider
                  value={formData.speedFactor || 1.72}
                  onChange={(value) => onFormDataChange({ speedFactor: value })}
                />
              )}

              {/* 匹配时长开关 */}
              <button
                type="button"
                onClick={() => formData.spkAudioUrl && onFormDataChange({ matchReferenceDuration: !formData.matchReferenceDuration })}
                disabled={!formData.spkAudioUrl}
                className={`p-1.5 rounded-lg transition-colors ${!formData.spkAudioUrl
                  ? 'bg-bg-elevated text-gray-600 cursor-not-allowed border border-border-default'
                  : formData.matchReferenceDuration
                    ? 'bg-blue-600 text-white'
                    : 'bg-bg-elevated text-gray-400 hover:bg-border-default hover:text-gray-300 border border-border-default'
                  }`}
                title={!formData.spkAudioUrl ? t('audio.form.matchDurationTooltipDisabled') : t('audio.form.matchDurationTooltip')}
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 8v4l3 3" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="12" cy="12" r="9" />
                </svg>
              </button>
            </div>
          </div>

          {/* 生成按钮和剧点 */}
          <div className="pt-4 space-y-2">
            <button
              type="submit"
              disabled={modelsLoading || !formData.prompt.trim() || !formData.spkAudioUrl || !hasEnoughCoins || !formData.modelId}
              className="w-full bg-gradient-to-br from-primary to-primary-light hover:shadow-[0_0_20px_rgba(255,92,0,0.3)] disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              {modelsLoading ? t('common:loading') : t('audio.form.generateAudio')}
            </button>
            {!modelsLoading && modelPrice > 0 && estimatedSeconds > 0 && (
              <div className="flex items-center justify-between text-xs px-1">
                <div className="flex items-center gap-1 text-gray-500">
                  <CurrencyDollarIcon className="w-3.5 h-3.5" />
                  <span>{t('audio.form.estimatedCost')} <span className="font-mono text-primary">{estimatedCost}</span></span>
                  <span className="text-gray-600">({estimatedSeconds}{t('audio.form.seconds')})</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-gray-500">{t('audio.form.balance')}</span>
                  <span className={`font-mono ${hasEnoughCoins ? 'text-gray-300' : 'text-error'}`}>
                    {account?.balance ?? 0}
                  </span>
                </div>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
